import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Heart, Star, X, Timer } from "lucide-react";
import { AdventCalendar } from "@/components/AdventCalendar";
import { Elf } from "@/components/Elf";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Snowfall } from "@/components/Snowfall";
import { VillageSilhouette } from "@/components/VillageSilhouette";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DEFAULT_PRICING, formatPrice, hasPromo, fetchPricing, promoCountdown } from "@/lib/pricing";

const DEMO_REWARDS = {
  1: {
    title: "Aperitivo 2x1",
    description: "Uno degli esempi di vantaggi che potresti trovare nel tuo calendario.",
  },
  8: {
    title: "Dolce omaggio per te",
    description: "Un piccolo regalo goloso, come tanti che si nascondono dietro le caselle.",
  },
  15: {
    title: "Momento benessere −20%",
    description: "Un esempio di coccola che i nostri partner potrebbero riservarti.",
  },
  24: {
    title: "La sorpresa della Vigilia",
    description: "Il gran finale resta un segreto: sarà la sorpresa più grande del tuo calendario.",
  },
};

const DEMO_DAYS = [1, 8, 15, 24];

const buildDemoDoors = () => {
  const year = new Date().getFullYear();
  return Array.from({ length: 24 }, (_, i) => {
    const day = i + 1;
    const isDemo = DEMO_DAYS.includes(day);
    return {
      day,
      isDemo,
      isLocked: !isDemo,
      reward: DEMO_REWARDS[day] ?? null,
      unlockDate: new Date(year, 11, day).toISOString(),
    };
  });
};

const STEPS = [
  "Compra il calendario per te oppure regalalo a chi vuoi",
  "Ricevi il tuo codice di attivazione personale",
  "Dal 1 al 24 dicembre apri una casella al giorno",
  "Scopri regali, vantaggi ed esperienze offerte dai nostri partner",
];

const MARQUEE_ITEMS = [
  "Un regalo nuovo ogni giorno",
  "Dal 1 al 24 dicembre",
  "Sorprese dai migliori brand",
  "Compralo o regalalo",
  "Disponibile in tutta Italia",
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const reveal = (delay) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [demoDoors] = useState(buildDemoDoors);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    fetchPricing().then(setPricing);
  }, []);

  const goBuy = () => navigate(user ? "/dashboard" : "/registrati");

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#06150F] text-[#FAF6EE]">
      <Snowfall />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(ellipse_at_top,rgba(90,15,25,0.6),transparent_65%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-red-900/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-2/3 h-96 w-96 rounded-full bg-red-900/15 blur-3xl"
      />

      <SiteHeader onBuy={goBuy} />

      <main className="relative z-10">
        {/* hero — centered, calendar is the star */}
        <section className="mx-auto max-w-7xl px-5 pb-16 pt-10 sm:px-8 lg:pt-14">
          <div className="mx-auto max-w-3xl space-y-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span
                data-testid="badge-italy"
                className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-amber-200"
              >
                <MapPin className="h-3.5 w-3.5" /> Disponibile in tutta Italia
              </span>
            </motion.div>

            <h1 className="leading-[1.08] tracking-tight">
              <span className="block overflow-hidden pb-1">
                <motion.span
                  className="font-hero block text-4xl font-semibold text-[#FAF6EE] sm:text-5xl lg:text-6xl"
                  {...reveal(0.15)}
                >
                  24 giorni. 24 sorprese.
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-3">
                <motion.span
                  className="gold-text font-script block text-5xl sm:text-6xl lg:text-7xl"
                  {...reveal(0.35)}
                >
                  Un Natale tutto da aprire.
                </motion.span>
              </span>
            </h1>

            <motion.p
              data-testid="hero-subtitle"
              className="mx-auto max-w-2xl text-base leading-relaxed text-emerald-100/80 sm:text-lg"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
            >
              Compra il tuo Calendario dell'Avvento digitale e scopri ogni giorno
              regali, vantaggi, esperienze e sorprese offerte dai nostri partner.
            </motion.p>
            <motion.p
              data-testid="gift-line"
              className="text-base text-amber-100/90"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
            >
              Puoi comprarlo per te o regalarlo a qualcuno di speciale.
            </motion.p>
            <motion.div
              className="flex flex-wrap items-center justify-center gap-3.5"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.78 }}
            >
              <button
                type="button"
                data-testid="cta-buy-calendar-button"
                onClick={goBuy}
                className="rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-7 py-3.5 font-bold text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-amber-500/30"
              >
                Compra il calendario
              </button>
              <button
                type="button"
                data-testid="cta-gift-calendar-button"
                onClick={goBuy}
                className="inline-flex items-center gap-2 rounded-xl border border-red-400/40 bg-[#4A0D16]/80 px-7 py-3.5 font-semibold text-red-100 transition-all duration-300 hover:scale-[1.03] hover:bg-[#5E121D]"
              >
                <Heart className="h-4 w-4" /> Regalalo
              </button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="flex justify-center"
            >
              <p
                data-testid="hero-price"
                className="inline-flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-6 py-2.5 shadow-[0_0_35px_rgba(212,175,55,0.15)]"
              >
                {hasPromo(pricing) && (
                  <span data-testid="hero-original-price" className="text-base text-emerald-100/50 line-through">
                    {formatPrice(pricing.original_price)}
                  </span>
                )}
                <span className="font-display text-2xl font-bold gold-text">{formatPrice(pricing.price)}</span>
                {hasPromo(pricing) && pricing.promo_label && (
                  <span
                    data-testid="hero-promo-label"
                    className="rounded-full bg-[#8B1E2B] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-100"
                  >
                    {pricing.promo_label}
                  </span>
                )}
                {promoCountdown(pricing) && (
                  <span data-testid="hero-promo-countdown" className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-200/90">
                    <Timer className="h-3 w-3" /> Scade {promoCountdown(pricing)}
                  </span>
                )}
                <span className="text-xs text-emerald-100/70 sm:text-sm">
                  una volta sola · 24 sorprese · anche da regalare
                </span>
              </p>
            </motion.div>
          </div>

          {/* the calendar, big and centered, with animated elves */}
          <motion.div
            className="relative mx-auto mt-14 max-w-4xl xl:max-w-5xl"
            initial={{ opacity: 0, y: 44, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformPerspective: 1200 }}
          >
            <VillageSilhouette className="pointer-events-none absolute -top-12 left-1/2 z-0 w-[145%] max-w-none -translate-x-1/2 opacity-95 sm:-top-20" />
            <Elf
              variant="bob"
              className="absolute -top-12 left-3 z-20 w-16 sm:-top-20 sm:left-8 sm:w-24"
              data-testid="elf-calendar-top"
            />
            <Elf
              variant="peek"
              flip
              className="absolute -right-5 top-24 z-0 w-14 sm:-right-14 sm:top-32 sm:w-24"
              data-testid="elf-calendar-side"
            />
            <div className="relative z-10">
              <AdventCalendar
                doors={demoDoors}
                pricing={pricing}
                onBuy={goBuy}
                onDiscover={() => scrollToId("partner")}
              />
            </div>
            <p data-testid="mobile-calendar-hint" className="mt-5 text-center text-sm text-emerald-100/70">
              Tocca una casella per scoprire come funziona.
            </p>

            <div data-testid="commercial-message" className="mx-auto mt-10 max-w-xl text-center">
              <p className="font-script gold-text text-3xl leading-snug sm:text-4xl">
                Non è un semplice calendario.
              </p>
              <p className="font-hero mt-1 text-xl italic text-amber-100 sm:text-2xl">
                È un regalo nuovo ogni giorno.
              </p>
              <p className="mt-3 text-sm text-emerald-100/70">Compralo per te oppure regalalo.</p>
              <p data-testid="reassurance-line" className="mt-4 text-sm text-emerald-100/60">
                Una nuova sorpresa ogni giorno dal 1 al 24 dicembre.
              </p>
            </div>
          </motion.div>
        </section>

        {/* editorial marquee */}
        <div
          aria-hidden
          className="relative overflow-hidden border-y border-amber-500/15 bg-[#0A1D15]/60 py-4"
        >
          <div className="marquee-track flex w-max items-center gap-12 whitespace-nowrap">
            {[...Array(2)].map((_, copy) => (
              <div key={copy} className="flex items-center gap-12">
                {MARQUEE_ITEMS.map((item, i) => (
                  <span key={i} className="flex items-center gap-12">
                    <span className="font-display text-sm uppercase italic tracking-[0.3em] text-amber-200/70">
                      {item}
                    </span>
                    <Star className="h-3.5 w-3.5 fill-amber-400/60 text-amber-400/60" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* partner brands — slots reserved for the admin */}
        <section id="partner" className="relative mx-auto max-w-6xl scroll-mt-24 px-5 pb-16 pt-16 sm:px-8">
          <Elf
            variant="jump"
            className="absolute -top-4 right-6 w-14 sm:right-16 sm:w-20"
            data-testid="elf-partner"
          />
          <motion.div
            className="mx-auto max-w-2xl text-center"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/90">
              I partner dell'Avvento
            </span>
            <h2
              data-testid="partner-title"
              className="font-display mt-3 text-3xl font-bold tracking-tight text-amber-100 sm:text-4xl"
            >
              I brand dietro le sorprese
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-emerald-100/70 sm:text-base">
              Stiamo selezionando i migliori brand d'Italia. I loro nomi verranno
              svelati qui, uno dopo l'altro.
            </p>
          </motion.div>

          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="brands-slots">
            {[...Array(8)].map((_, i) => {
              const brand = brands[i];
              return (
                <motion.div
                  key={i}
                  data-testid={`brand-slot-${i + 1}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.1 }}
                  className={`flex h-20 items-center justify-center rounded-xl transition-colors duration-300 sm:h-24 ${
                    brand
                      ? "border border-amber-400/40 bg-[#0C241A]/70 hover:border-amber-400/70"
                      : "border border-dashed border-amber-400/30 bg-white/[0.02] hover:border-amber-400/50"
                  }`}
                >
                  {brand ? (
                    <span className="flex items-center gap-2.5 px-4">
                      {brand.logo_url ? (
                        <img src={brand.logo_url} alt={brand.name} className="max-h-9 max-w-[70%] object-contain" />
                      ) : (
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10 font-display text-sm font-bold text-amber-300">
                          {brand.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="truncate text-sm font-semibold text-amber-100">{brand.name}</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-200/40">
                      <Star className="h-3.5 w-3.5" /> Brand partner
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 flex flex-col items-center gap-4 text-center">
            <button
              type="button"
              data-testid="cta-how-it-works-button"
              onClick={() => setHowItWorksOpen(true)}
              className="rounded-xl border border-amber-400/40 bg-emerald-950/70 px-8 py-3.5 font-semibold text-amber-200 transition-all duration-300 hover:scale-[1.03] hover:bg-emerald-900"
            >
              Scopri come funziona
            </button>
            <button
              type="button"
              data-testid="partner-cta-button"
              onClick={() => navigate("/diventa-partner")}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-amber-300/90 transition-colors hover:text-amber-200"
            >
              Hai un brand? Portalo sotto l'albero — è gratuito
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* how it works modal */}
      <AnimatePresence>
        {howItWorksOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-[#030B08]/80 backdrop-blur-sm"
              onClick={() => setHowItWorksOpen(false)}
            />
            <motion.div
              data-testid="how-it-works-modal"
              initial={{ opacity: 0, y: 32, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md rounded-2xl border border-amber-400/40 bg-gradient-to-b from-[#112A1E] to-[#0A1D15] p-7 shadow-[0_0_70px_rgba(212,175,55,0.25)] sm:p-9"
            >
              <button
                type="button"
                data-testid="how-it-works-close-button"
                aria-label="Chiudi"
                onClick={() => setHowItWorksOpen(false)}
                className="absolute right-3 top-3 rounded-full p-1.5 text-emerald-100/60 transition-colors hover:bg-white/5 hover:text-amber-200"
              >
                <X className="h-4 w-4" />
              </button>
              <h3 className="font-display text-2xl font-bold text-amber-100">Come funziona</h3>
              <ol className="mt-6 space-y-4">
                {STEPS.map((step, i) => (
                  <li key={i} className="flex items-start gap-4" data-testid={`how-it-works-step-${i + 1}`}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-400/50 bg-amber-400/10 font-display text-sm font-bold text-amber-300">
                      {i + 1}
                    </span>
                    <p className="pt-1 text-sm leading-relaxed text-emerald-100/85">{step}</p>
                  </li>
                ))}
              </ol>
              <button
                type="button"
                data-testid="how-it-works-buy-button"
                onClick={() => {
                  setHowItWorksOpen(false);
                  goBuy();
                }}
                className="mt-8 w-full rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-6 py-3 font-bold text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/30"
              >
                Compra il calendario · {formatPrice(pricing.price)}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
